// utils
import { getSortedCalendars } from 'components/mrp/mrp.utils';
import { getShortCuts } from './utils/getShortCuts';
import { navigateCells } from './utils/navigateCells';
import { useTabThroughEvents } from './utils/tabThroughEvents.hooks';

// providers
import { useCreateShortcutContext } from 'components/shared/ShortcutProvider';

// stores
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { useShallow } from 'zustand/shallow';
import { useCalendarNavigationStore } from '../store/calendarNavigationStore.store';

// hooks
import { useCalendarCleanup } from './hooks/useCalendarCleanup';
import { useEventSelectionSync } from './hooks/useEventSelectionSync';
import { usePendingEventRestore } from './hooks/usePendingEventRestore';
import { usePositionBoundsAdjustment } from './hooks/usePositionBoundsAdjustment';
import { usePositionReset } from './hooks/usePositionReset';
import { useTimeStepRounding } from './hooks/useTimeStepRounding';

// types
import type { ReactNode } from 'react';
import type { NavigationDirection } from './CalendarNavigationHandler.types';

const CalendarNavigationHandler = ({
  children
}: {
  children: ReactNode | ReactNode[];
}) => {
  'use memo';
  const {
    position,
    calendarEvents,
    event,
    setPosition,
    setEvent,
    removeCalendarEventsForCalendarIds
  } = useCalendarNavigationStore(
    useShallow((state) => ({
      position: state.position,
      calendarEvents: state.calendarEvents,
      event: state.event,
      setPosition: state.control.setPosition,
      setEvent: state.control.setEvent,
      removeCalendarEventsForCalendarIds:
        state.control.removeCalendarEventsForCalendarIds
    }))
  );

  const appointmentId = useAppointmentStore((state) => state.appointment?.id);

  const { calendars, timeStep, timeSlotMin, timeSlotMax } = useCalendarStore(
    useShallow((state) => ({
      calendars: state.calendars,
      timeStep: state.timeStep,
      timeSlotMin: state.timeSlotMin,
      timeSlotMax: state.timeSlotMax
    }))
  );

  const { startDate, endDate } = useSelectedDateRange();

  const sortedCalendars = getSortedCalendars(calendars);

  const { tabThroughEvents, isTabThroughActive: isCalendarNavigationActive } =
    useTabThroughEvents(sortedCalendars, calendarEvents);

  const {
    pendingRestoreEventId,
    pendingRestoreOriginalAppointmentId,
    clearPendingRestore
  } = usePendingEventRestore({ appointmentId, calendarEvents, position });

  usePositionBoundsAdjustment({
    position,
    startDate,
    endDate,
    timeSlotMin,
    timeSlotMax,
    timeStep,
    setPosition
  });

  const resetPosition = usePositionReset({
    calendars,
    startDate,
    timeSlotMin,
    position,
    setPosition
  });

  useCalendarCleanup({
    calendars,
    position,
    calendarEvents,
    resetPosition,
    setEvent,
    removeCalendarEventsForCalendarIds
  });

  useTimeStepRounding({
    position,
    calendarEvents,
    event,
    timeStep,
    setEvent,
    setPosition,
    pendingRestoreEventId
  });

  useEventSelectionSync({
    calendarEvents,
    event,
    position,
    setEvent,
    pendingRestoreEventId,
    pendingRestoreOriginalAppointmentId,
    clearPendingRestore
  });

  const navigate = (direction: NavigationDirection) => {
    const newPosition = navigateCells({
      direction,
      calendars: sortedCalendars,
      endDate,
      position,
      event,
      startDate,
      timeStep,
      timeSlotMin,
      timeSlotMax
    });

    if (!newPosition) return;

    setPosition(newPosition);
  };

  const tabThroughEventsCallback = (backwards = false) => {
    tabThroughEvents({ backwards, event, position, setEvent, setPosition });
  };

  useCreateShortcutContext(
    'calendar-navigation',
    getShortCuts({
      navigate,
      tabThroughEvents: tabThroughEventsCallback,
      active: isCalendarNavigationActive
    }),
    [position, event, calendarEvents, calendars]
  );

  return <>{children}</>;
};

export default CalendarNavigationHandler;
